import { useEffect, useState } from 'react'

export type KeyboardViewportInsetState = {
  bottomInset: number
  isKeyboardOpen: boolean
}

type KeyboardViewportMetrics = {
  innerHeight: number
  visualViewportHeight: number
  visualViewportOffsetTop: number
}

const DEFAULT_KEYBOARD_VIEWPORT_INSET: KeyboardViewportInsetState = {
  bottomInset: 0,
  isKeyboardOpen: false,
}

const KEYBOARD_OPEN_THRESHOLD_PX = 80

function toFiniteNonNegativeNumber(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0
}

export function calculateKeyboardViewportBottomInset({
  innerHeight,
  visualViewportHeight,
  visualViewportOffsetTop,
}: KeyboardViewportMetrics) {
  const layoutHeight = toFiniteNonNegativeNumber(innerHeight)
  const viewportHeight = toFiniteNonNegativeNumber(visualViewportHeight)
  const viewportOffsetTop = toFiniteNonNegativeNumber(visualViewportOffsetTop)

  if (layoutHeight === 0 || viewportHeight === 0) return 0

  return Math.max(
    0,
    Math.ceil(layoutHeight - viewportHeight - viewportOffsetTop),
  )
}

export function getKeyboardViewportInset(): KeyboardViewportInsetState {
  if (typeof window === 'undefined' || !window.visualViewport) {
    return DEFAULT_KEYBOARD_VIEWPORT_INSET
  }

  const bottomInset = calculateKeyboardViewportBottomInset({
    innerHeight: window.innerHeight,
    visualViewportHeight: window.visualViewport.height,
    visualViewportOffsetTop: window.visualViewport.offsetTop,
  })

  return {
    bottomInset,
    isKeyboardOpen: bottomInset >= KEYBOARD_OPEN_THRESHOLD_PX,
  }
}

export function useKeyboardViewportInset() {
  const [state, setState] = useState<KeyboardViewportInsetState>(() =>
    getKeyboardViewportInset(),
  )

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      setState(DEFAULT_KEYBOARD_VIEWPORT_INSET)
      return
    }

    const visualViewport = window.visualViewport
    let frame: number | null = null

    const update = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        setState(getKeyboardViewportInset())
      })
    }

    update()
    visualViewport.addEventListener('resize', update)
    visualViewport.addEventListener('scroll', update)
    window.addEventListener('resize', update)

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
      visualViewport.removeEventListener('resize', update)
      visualViewport.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  return state
}
